import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

function complexityMeta(score) {
  if (score <= 3) return { label: 'Easy', color: '#39FF14' };
  if (score <= 6) return { label: 'Moderate', color: '#FFE800' };
  return { label: 'Complex', color: '#ff6b6b' };
}

export default function ConsultingTriggerCard({
  complexityScore,
  needle,
  userProfile,
  onBook,
  onSkip,
  onExplain,
}) {
  const meta = complexityMeta(complexityScore || 0);
  const etaWeeks = Math.max(1, Math.ceil((complexityScore || 0) * 0.8));
  const stackCategory = userProfile?.focus?.[0] || 'growth';
  const first = needle?.[0]?.name || 'your first tool';
  const second = needle?.[1]?.name || 'your second tool';
  const teamsCount = userProfile?.implementedCount || '140+';

  const link = useMemo(() => {
    const slugs = (needle || []).map((t) => t.slug || t.id).filter(Boolean).join(',');
    return `/consulting?from=finder&stack=${encodeURIComponent(slugs)}&complexity=${complexityScore || 0}`;
  }, [needle, complexityScore]);

  return (
    <motion.aside
      initial={{ y: 20, opacity: 0, boxShadow: '0 0 0 rgba(57,255,20,0)' }}
      animate={{ y: 0, opacity: 1, boxShadow: ['0 0 0 rgba(57,255,20,0)', '0 0 12px rgba(57,255,20,0.3)', '0 0 0 rgba(57,255,20,0)'] }}
      transition={{ duration: 0.6, delay: 1.2 }}
      className="p-4 border rounded-none"
      style={{
        background: 'rgba(57,255,20,0.04)',
        border: '1px solid rgba(57,255,20,0.25)',
        borderLeft: '3px solid #39FF14',
      }}
      role="complementary"
      aria-label="Implementation help available"
      data-testid="finder-consulting-trigger"
    >
      <h3 className="text-sm font-bold mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
        Your stack is selected. Implementation is the hard part.
      </h3>
      <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
        You&apos;ve added {needle?.length || 0} tools. Connecting {first} with {second} usually needs API setup, workflow checks, and QA - most teams spend 3-4 weeks figuring this out alone.
      </p>

      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
          <span>Easy · Moderate · Complex</span>
          <span style={{ color: meta.color }}>{meta.label}</span>
        </div>
        <div className="h-2" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <div
            className="h-full"
            style={{
              width: `${Math.max(0, Math.min(100, (complexityScore || 0) * 10))}%`,
              background: meta.color,
            }}
          />
        </div>
        <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
          Estimated setup time: {etaWeeks} week{etaWeeks > 1 ? 's' : ''} without guidance
        </p>
      </div>

      <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
        Our consultants have implemented this exact {stackCategory} stack for {teamsCount} teams. Average: operational in 4 weeks.
      </p>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onBook(link)}
          className="px-3 py-2 text-xs font-bold rounded-none"
          style={{ background: 'var(--glow-2)', color: 'var(--bg-base)' }}
          data-testid="finder-book-consulting"
        >
          Get it implemented in 4 weeks →
        </button>
        <button
          onClick={onSkip}
          className="px-3 py-2 text-xs border rounded-none"
          style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
          data-testid="finder-skip-consulting"
        >
          I&apos;ll set it up myself
        </button>
        <button
          onClick={onExplain}
          className="px-3 py-2 text-xs border rounded-none"
          style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
          data-testid="finder-consulting-explain"
        >
          Tell me what&apos;s involved
        </button>
      </div>
    </motion.aside>
  );
}
